/**
 * Bestandsmodell am Scan: vier Schritte, Gespräch mit Claude, Schlüssel für die
 * SketchUp-Erweiterung. Aus ProjektMedien.tsx ausgelagert (03.11.2026), unverändert bis
 * auf den Kopf: das Bauset steht seitdem bei den Rohdaten des Scans — es ist kein Modell,
 * das man in SketchUp öffnet, sondern Futter für die Erweiterung.
 */
import { useEffect, useState } from "react";
import { fehlerText } from "@vimaana/ui";
import DokumentLightbox from "../DokumentLightbox";
import { useBestandsmodell, useSchluesselAnlegen, useSchluesselWiderrufen, useSketchupSchluessel, type Medium, type ModellStand } from "../../hooks/useMedien";

/** Die vier Schritte vom Scan zum SketchUp-Modell — was erledigt ist, was gerade läuft. */
function schritte(m: Medium, modell: ModellStand): { name: string; stand: "fertig" | "laeuft" | "offen" | "fehler" }[] {
  const aufbereitet = m.zustand === "fertig";
  const z = modell.zustand;
  const bauset = z === "fertig" || z === "rueckfrage" || modell.dateien.some((d) => d.teil === "bauset");
  return [
    { name: "Scan hochgeladen", stand: "fertig" },
    { name: "Aufbereitet", stand: aufbereitet ? "fertig" : m.zustand === "fehler" ? "fehler" : "laeuft" },
    {
      name: "Claude baut das Modell",
      stand: z === "arbeitet" || (z === "wartet" && aufbereitet) ? "laeuft" : z === "fehler" ? "fehler" : bauset ? "fertig" : "offen",
    },
    { name: "In SketchUp", stand: modell.in_sketchup ? "fertig" : "offen" },
  ];
}

/**
 * Bestandsmodell aus dem Scan: Claude baut auf dem Evo aus Scan, Fotos und Geodaten ein Bauset
 * für SketchUp (Benjamin, 16.10.2026: „Von hier muss es sehr einfach sein, den automatisierten
 * Prozess anzustoßen … und ebenso muss es ein Chat-Feld dazu geben“). Ein Knopf, vier Schritte,
 * darunter der Chat mit Claude — jede Nachricht startet den nächsten Lauf in derselben Sitzung.
 * Der Knopf geht schon, während der Scan noch aufbereitet wird (der Auftrag wird vorgemerkt).
 */
export default function Bestandsmodell({ m, modell, projektId }: { m: Medium; modell: ModellStand; projektId: string }) {
  const los = useBestandsmodell(projektId);
  const [text, setText] = useState("");
  const [alles, setAlles] = useState(false);
  const [verbinden, setVerbinden] = useState(false);
  const [lageplanGross, setLageplanGross] = useState(false);
  // Gespräch nur bei Bedarf (Benjamin, 16.10.2026: „minimieren, so dass er nur bei Bedarf zu sehen ist“) —
  // von selbst offen nur, wenn Claude eine Rückfrage hat oder der Lauf gescheitert ist.
  const braucht = modell.zustand === "rueckfrage" || modell.zustand === "fehler";
  const [gespraech, setGespraech] = useState(braucht);
  useEffect(() => { if (braucht) setGespraech(true); }, [braucht]);
  const laeuft = modell.zustand === "wartet" || modell.zustand === "arbeitet";
  const datei = (teil: string) => modell.dateien.find((d) => d.teil === teil);
  const lageplan = datei("lageplan");
  const bauset = datei("bauset");
  const bericht = datei("bericht");
  const senden = () => los.mutate({ id: m.id, text: text.trim() }, { onSuccess: () => setText("") });
  const verlauf = alles ? modell.verlauf : modell.verlauf.slice(-3);
  const hinweis = modell.laeufe ? modell.hinweis
    : m.zustand === "fertig" ? "Claude baut aus Scan, Fotos und Geodaten ein Modell für SketchUp — verortet, mit Umgebung. Dauert meist 10 bis 20 Minuten."
    : "Der Scan wird gerade aufbereitet. Du kannst trotzdem schon starten — Claude fängt an, sobald er fertig ist.";

  return (
    <div className="bm-block">
      <div className="bm-kopf">
        <strong>Bestandsmodell{modell.titel ? ` „${modell.titel}“` : ""}</strong>
        {bauset && (
          <span className="me-links">
            {bericht && <a className="vg-btn" href={bericht.url} target="_blank" rel="noreferrer">Bericht</a>}
            <button type="button" className="vg-btn" aria-expanded={verbinden} onClick={() => setVerbinden(!verbinden)}>SketchUp verbinden</button>
          </span>
        )}
      </div>
      <ol className="bm-schritte">
        {schritte(m, modell).map((s) => (
          <li key={s.name} className={`is-${s.stand}`}><span aria-hidden="true" />{s.name}</li>
        ))}
      </ol>
      {hinweis && <small className="bm-leise">{hinweis}</small>}
      {verbinden && <SketchupVerbinden />}

      {!modell.laeufe ? (
        <div className="bm-chat">
          <label className="bm-feld">
            <span>Hinweis an Claude (wenn du magst)</span>
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="z. B. „Die Wege sind aus Granit-Kleinpflaster, das Becken ist leer“" />
          </label>
          <div className="bm-leiste">
            <button type="button" className="vg-btn vg-btn-primary" disabled={los.isPending} onClick={senden}>
              {los.isPending ? "Wird gestartet …" : "Bestandsmodell erstellen"}
            </button>
          </div>
        </div>
      ) : (
        <>
        <div className="bm-leiste bm-gespraech-leiste">
          <DokumentLightbox
            dok={lageplanGross && lageplan ? { titel: "Lageplan", unterzeile: "Scan auf dem Luftbild", url: lageplan.url, bild: true } : null}
            onClose={() => setLageplanGross(false)}
          />
          {lageplan && (
            <button type="button" className="bm-lageplan" onClick={() => setLageplanGross(true)} title="Lageplan groß öffnen">
              <img src={lageplan.url} alt="Lageplan: Scan auf dem Luftbild" loading="lazy" />
            </button>
          )}
          <button type="button" className="vg-btn" aria-expanded={gespraech} onClick={() => setGespraech(!gespraech)}>
            {gespraech ? "Gespräch ausblenden" : `Gespräch mit Claude${modell.verlauf.length ? ` (${modell.verlauf.length})` : ""}`}
            {!gespraech && modell.zustand === "rueckfrage" ? " · Rückfrage" : ""}
          </button>
        </div>
        {gespraech && <div className="bm-chat">
          <div className="bm-chat-kopf">
            <span>Gespräch mit Claude</span>
            {modell.verlauf.length > 3 && (
              <button type="button" className="bm-mehr" onClick={() => setAlles(!alles)}>
                {alles ? "Nur die letzten" : `Alle ${modell.verlauf.length} anzeigen`}
              </button>
            )}
          </div>
          <div className="bm-verlauf">
            {verlauf.map((e, i) => (
              <div key={i} className={`bm-eintrag is-${e.wer}`}>
                <small>{e.wer === "claude" ? "Claude" : e.wer === "system" ? "System" : e.von || "Du"} · {zeit(e.zeit)}</small>
                <div>{e.text}</div>
              </div>
            ))}
            {laeuft && (
              <div className="bm-eintrag is-claude is-tippt">
                <small>Claude</small>
                <div>{modell.zustand === "arbeitet" ? "arbeitet am Modell …" : "fängt gleich an …"}</div>
              </div>
            )}
          </div>
          <label className="bm-feld">
            <span className="bm-sr">{modell.zustand === "rueckfrage" ? "Antwort an Claude" : "Nachricht an Claude"}</span>
            <textarea
              rows={2}
              value={text}
              disabled={laeuft || los.isPending}
              onChange={(e) => setText(e.target.value)}
              placeholder={laeuft ? "Claude arbeitet gerade — danach kannst du schreiben."
                : modell.zustand === "rueckfrage" ? "Antwort auf Claudes Rückfrage …"
                : "Was soll Claude ändern? z. B. „Der Pool ist 1,70 m tief“"}
            />
          </label>
          <div className="bm-leiste">
            <button type="button" className="vg-btn vg-btn-primary" disabled={laeuft || los.isPending || !text.trim()} onClick={senden}>
              {los.isPending ? "Wird gesendet …" : "Senden"}
            </button>
            {modell.zustand === "fehler" && (
              <button type="button" className="vg-btn" disabled={los.isPending} onClick={() => los.mutate({ id: m.id, text: "" })}>Neu versuchen</button>
            )}
          </div>
        </div>}
        </>
      )}
      {los.isError && <div className="vg-banner is-warn">{fehlerText(los.error)}</div>}
    </div>
  );
}

/**
 * Schlüssel für die SketchUp-Erweiterung: einmal anlegen, in SketchUp einfügen — danach holt
 * „Bestandsmodell aus Kontor laden …“ das Bauset und legt die .skp hier ab (16.10.2026).
 * Ein Schlüssel je Person und Rechner; widerrufen macht ihn sofort wertlos.
 */
function SketchupVerbinden() {
  const liste = useSketchupSchluessel(true);
  const anlegen = useSchluesselAnlegen();
  const widerrufen = useSchluesselWiderrufen();
  const [name, setName] = useState("SketchUp am Mac");
  const [neu, setNeu] = useState("");
  const [kopiert, setKopiert] = useState(false);
  const kopieren = async () => {
    try { await navigator.clipboard.writeText(neu); setKopiert(true); } catch { setKopiert(false); }
  };
  return (
    <div className="bm-verbinden">
      <small>
        Einmal je Rechner: Schlüssel anlegen, kopieren und in SketchUp bei Erweiterungen → Vimaana Garten →
        „Bestandsmodell aus Kontor laden …“ einfügen. Danach holt SketchUp das Bauset und legt die fertige Datei hier ab.
      </small>
      {neu ? (
        <div className="bm-schluessel">
          <code>{neu}</code>
          <div className="bm-leiste">
            <button type="button" className="vg-btn vg-btn-primary" onClick={kopieren}>{kopiert ? "Kopiert" : "Kopieren"}</button>
            <button type="button" className="vg-btn" onClick={() => { setNeu(""); setKopiert(false); }}>Fertig</button>
          </div>
          <small>Der Schlüssel wird nur jetzt angezeigt. Geht er verloren: widerrufen und neu anlegen.</small>
        </div>
      ) : (
        <div className="bm-leiste">
          <input className="bm-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} aria-label="Name des Schlüssels" />
          <button type="button" className="vg-btn vg-btn-primary" disabled={!name.trim() || anlegen.isPending}
            onClick={() => anlegen.mutate(name.trim(), { onSuccess: (r) => setNeu(r.schluessel) })}>
            {anlegen.isPending ? "Wird angelegt …" : "Schlüssel anlegen"}
          </button>
        </div>
      )}
      {(liste.data ?? []).filter((k) => !k.widerrufen).map((k) => (
        <div key={k.id} className="bm-schluessel-zeile">
          <span>{k.name}<small> · angelegt {zeit(k.angelegt_am)}{k.zuletzt_am ? ` · zuletzt benutzt ${zeit(k.zuletzt_am)}` : " · noch nicht benutzt"}</small></span>
          <button type="button" className="vg-btn" disabled={widerrufen.isPending} onClick={() => widerrufen.mutate(k.id)}>Widerrufen</button>
        </div>
      ))}
      {(anlegen.isError || widerrufen.isError || liste.isError) && (
        <div className="vg-banner is-warn">{fehlerText(anlegen.error || widerrufen.error || liste.error)}</div>
      )}
    </div>
  );
}

function zeit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}
